import React, { useCallback, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useTheme } from '@mui/material';
import { debounce } from '@mui/material/utils';
import { bindFocus, bindHover, bindMenu, usePopupState } from 'material-ui-popup-state/hooks';
import CascadingMenuContext from '@/pages/document/components/DocumentMoreButton/components/CascadingMenuContext';
import HoverMenu from 'material-ui-popup-state/HoverMenu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import TooltipForButton from '@/components/mui/Tooltip/TooltipForButton';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

export const CascadingHoverMenu = ({ popupState, options, isRtl, slotProps = {}, ...props }) => {
  const { rootPopupState } = useContext(CascadingMenuContext);
  const context = React.useMemo(
    () => ({
      rootPopupState: rootPopupState || popupState,
      parentPopupState: popupState,
    }),
    [rootPopupState, popupState],
  );

  const list = options.filter((option) => !option.isHidden);

  return (
    <CascadingMenuContext.Provider value={context}>
      <HoverMenu
        anchorOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'right' : 'left' }}
        slotProps={{
          paper: { sx: { ...(isRtl ? { mr: -0.5 } : { ml: 0.5 }) } },
          ...slotProps,
        }}
        {...props}
        {...bindMenu(popupState)}
      >
        {list.map((option) => (
          <MoreMenuItem option={option} key={option.id} />
        ))}
      </HoverMenu>
    </CascadingMenuContext.Provider>
  );
};

export const MoreMenuItem = ({ option }) => {
  const { rootPopupState, parentPopupState } = useContext(CascadingMenuContext);
  const { direction } = useTheme();
  const isRtl = direction === 'rtl';
  const { divided, disabled, isDanger, label, submenu, onClick, id, tooltip } = option;

  const popupState = usePopupState({
    id,
    variant: 'popover',
    parentPopupState,
  });

  const debouncedOnClick = useMemo(
    () =>
      debounce((e) => {
        rootPopupState.close(e);
        onClick?.();
      }, 500),
    [rootPopupState, onClick],
  );

  const handleClick = useCallback(
    (e) => {
      debouncedOnClick(e);
    },
    [debouncedOnClick],
  );

  const boundProps = submenu
    ? {
        ...bindHover(popupState),
        ...bindFocus(popupState),
      }
    : {};

  return (
    <>
      {divided && <Divider component="div" />}
      <TooltipForButton t={tooltip} placement="left" WrapperProps={{ sx: { display: 'block' } }}>
        <MenuItem component="div" disabled={disabled} onClick={handleClick} {...boundProps}>
          <Typography variant="body2" flexGrow={1} {...(isDanger ? { color: 'error' } : {})}>
            {/* string label = backend-owned dynamic text (sub-type name), rendered as-is (no i18n) */}
            {typeof label === 'string' ? label : <FormattedMessage {...label} />}
          </Typography>

          {submenu && (
            <>
              {isRtl ? <KeyboardArrowLeftIcon sx={{ mr: -1 }} /> : <KeyboardArrowRightIcon sx={{ mr: -1 }} />}
              <CascadingHoverMenu popupState={popupState} options={submenu} isRtl={isRtl} />
            </>
          )}
        </MenuItem>
      </TooltipForButton>
    </>
  );
};
